/**
 * PaymentsTable — таблица платежей (@tanstack/react-table). Колонки строго по макету:
 *   № · Дата план · Сумма план · Дата факт · Сумма факт · Статус (chip) · ⋯
 *
 * Инлайн-редактирование план-даты и план-суммы с автосохранением по blur/Enter (PATCH).
 * Замок оплаченного (isPaidLocked) закрывает правку без edit_paid. Клиентская фильтрация (q/статус/даты).
 * Новая строка (AddPaymentRow) монтируется в конце tbody, когда addMode=true.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type CellContext,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import type { PaymentDto } from '../../lib/types';
import { formatRub } from '../../lib/money';
import { isPaidLocked } from '../../lib/permissions';
import { fmtDate } from './lib/dates';
import { cx } from './ui/cx';
import { IconLock } from './ui/icons';
import { StatusChip } from './StatusChip';
import { DateCell } from './DateCell';
import { MoneyInput } from './MoneyInput';
import { RowActionsMenu } from './RowActionsMenu';
import { AddPaymentRow, type AddPaymentDraft } from './AddPaymentRow';
import type { PaymentFilters } from './FiltersBar';

/** Метаданные таблицы: права + колбэки строки. */
export interface PaymentsTableMeta {
  canEdit: boolean;
  canEditPaid: boolean;
  onCommitField: (payment: PaymentDto, patch: { plannedDate?: string; plannedAmountMinor?: string }) => void;
  onRegisterPayment: (p: PaymentDto) => void;
  onEdit: (p: PaymentDto) => void;
  onReschedule: (p: PaymentDto) => void;
  onHistory: (p: PaymentDto) => void;
  onCancel: (p: PaymentDto) => void;
  onDelete: (p: PaymentDto) => void;
}

/** Выравнивание колонки (метаданные колонки). */
interface PaymentsColumnMeta {
  align?: 'right';
}

// Строго типизируем meta таблицы/колонок (react-table по умолчанию оставляет их пустыми интерфейсами).
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> extends PaymentsTableMeta {}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> extends PaymentsColumnMeta {}
}

export interface PaymentsTableProps {
  data: PaymentDto[];
  filters: PaymentFilters;
  meta: PaymentsTableMeta;
  /** Режим добавления новой строки. */
  addMode: boolean;
  addPending: boolean;
  onAddConfirm: (draft: AddPaymentDraft) => void;
  onAddCancel: () => void;
}

const helper = createColumnHelper<PaymentDto>();

/** Стиль кликабельной ячейки факта (открывает «Внести оплату»). */
const FACT_BTN_STYLE: CSSProperties = {
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: '#2b7cff',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
};

/** Инлайн-редактируемая денежная ячейка (кнопка → MoneyInput по клику). */
function MoneyEditCell({ ctx }: { ctx: CellContext<PaymentDto, string> }): JSX.Element {
  const meta = ctx.table.options.meta as PaymentsTableMeta;
  const payment = ctx.row.original;
  const value = ctx.getValue();
  const [editing, setEditing] = useState(false);

  const locked = isPaidLocked(payment, meta.canEditPaid);
  const editable = meta.canEdit && !locked && payment.status !== 'CANCELLED';

  if (!editable) {
    return <span className="pw-num">{formatRub(value)}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="pw-cell-edit pw-num"
        title="Изменить плановую сумму"
        aria-label={`Изменить плановую сумму транша №${payment.number}`}
        onClick={() => setEditing(true)}
      >
        {formatRub(value)}
      </button>
    );
  }

  return (
    <MoneyInput
      initialMinor={value}
      autoFocus
      ariaLabel={`Плановая сумма транша №${payment.number}`}
      onCommit={(minor) => {
        setEditing(false);
        if (minor !== value) meta.onCommitField(payment, { plannedAmountMinor: minor });
      }}
      onCancel={() => setEditing(false)}
    />
  );
}

export function PaymentsTable({
  data,
  filters,
  meta,
  addMode,
  addPending,
  onAddConfirm,
  onAddCancel,
}: PaymentsTableProps): JSX.Element {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'number', desc: false }]);

  // Клиентская фильтрация: поиск по № и комментарию, статус, диапазон плановой даты.
  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return data.filter((p) => {
      if (q) {
        const inNumber = String(p.number).includes(q);
        const inComment = (p.comment ?? '').toLowerCase().includes(q);
        if (!inNumber && !inComment) return false;
      }
      if (filters.status !== 'ALL' && p.status !== filters.status) return false;
      if (filters.from && p.plannedDate < filters.from) return false;
      if (filters.to && p.plannedDate > filters.to) return false;
      return true;
    });
  }, [data, filters]);

  const columns = useMemo(
    () => [
      helper.accessor('number', {
        header: '№',
        cell: (c) => <span className="pw-td--seq">{c.getValue()}</span>,
      }),
      helper.accessor('plannedDate', {
        header: 'Дата план',
        cell: (c) => {
          const m = c.table.options.meta as PaymentsTableMeta;
          const p = c.row.original;
          const locked = isPaidLocked(p, m.canEditPaid);
          const editable = m.canEdit && !locked && p.status !== 'CANCELLED';
          return (
            <DateCell
              value={c.getValue()}
              editable={editable}
              ariaLabel={`Изменить плановую дату транша №${p.number}`}
              onChange={(ymd) => m.onCommitField(p, { plannedDate: ymd })}
            />
          );
        },
      }),
      helper.accessor('plannedAmountMinor', {
        header: 'Сумма план',
        meta: { align: 'right' },
        cell: (c) => <MoneyEditCell ctx={c} />,
      }),
      helper.accessor('actualDate', {
        header: 'Дата факт',
        cell: (c) => {
          const p = c.row.original;
          const m = c.table.options.meta as PaymentsTableMeta;
          const val = c.getValue();
          const editable = m.canEdit && p.status !== 'CANCELLED';
          if (!editable) return <span className={cx('pw-num', !val && 'pw-muted')}>{fmtDate(val)}</span>;
          return (
            <button
              type="button"
              className="pw-factbtn"
              style={FACT_BTN_STYLE}
              title="Внести оплату (дата факта)"
              onClick={() => m.onRegisterPayment(p)}
            >
              {val ? fmtDate(val) : '＋ внести'}
            </button>
          );
        },
      }),
      helper.accessor('paidAmountMinor', {
        header: 'Сумма факт',
        meta: { align: 'right' },
        cell: (c) => {
          const p = c.row.original;
          const m = c.table.options.meta as PaymentsTableMeta;
          const v = c.getValue();
          const editable = m.canEdit && p.status !== 'CANCELLED';
          const label = v === '0' ? '＋ внести' : formatRub(v);
          if (!editable) {
            return <span className={cx('pw-num', v === '0' && 'pw-muted')}>{v === '0' ? '—' : formatRub(v)}</span>;
          }
          return (
            <button
              type="button"
              className="pw-factbtn pw-num"
              style={FACT_BTN_STYLE}
              title="Внести оплату (сумма факта)"
              onClick={() => m.onRegisterPayment(p)}
            >
              {label}
            </button>
          );
        },
      }),
      helper.accessor('status', {
        header: 'Статус',
        cell: (c) => {
          const p = c.row.original;
          const m = c.table.options.meta as PaymentsTableMeta;
          const locked = isPaidLocked(p, m.canEditPaid);
          return (
            <span className="pw-status-cell">
              <StatusChip status={c.getValue()} />
              {locked ? (
                <span className="pw-status-cell__lock" title="Оплаченный транш — правка требует прав">
                  <IconLock size={13} />
                </span>
              ) : null}
            </span>
          );
        },
      }),
      helper.display({
        id: 'actions',
        header: '',
        meta: { align: 'right' },
        cell: (c) => {
          const p = c.row.original;
          const m = c.table.options.meta as PaymentsTableMeta;
          const locked = isPaidLocked(p, m.canEditPaid);
          return (
            <RowActionsMenu
              payment={p}
              canEdit={m.canEdit}
              locked={locked}
              onRegisterPayment={m.onRegisterPayment}
              onEdit={m.onEdit}
              onReschedule={m.onReschedule}
              onHistory={m.onHistory}
              onCancel={m.onCancel}
              onDelete={m.onDelete}
            />
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta,
  });

  const rows = table.getRowModel().rows;
  const nextNumber = data.reduce((max, p) => Math.max(max, p.number), 0) + 1;
  const noRows = rows.length === 0 && !addMode;

  return (
    <div className="pw-table-wrap" role="region" aria-label="Таблица платежей" tabIndex={0}>
      <table className="pw-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const align = header.column.columnDef.meta?.align;
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    className={cx('pw-th', align === 'right' && 'pw-th--right', canSort && 'pw-th--sortable')}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {canSort && sorted ? (
                      <span className="pw-th__sort" aria-hidden>
                        {sorted === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {noRows ? (
            <tr>
              <td className="pw-td pw-td--empty" colSpan={columns.length}>
                Ничего не найдено по заданным условиям.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.id}
                className={cx(
                  'pw-row',
                  row.original.status === 'OVERDUE' && 'pw-row--overdue',
                  row.original.status === 'CANCELLED' && 'pw-row--cancelled',
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  const align = cell.column.columnDef.meta?.align;
                  return (
                    <td
                      key={cell.id}
                      className={cx('pw-td', align === 'right' && 'pw-td--right', cell.column.id === 'actions' && 'pw-td--actions')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))
          )}

          {addMode ? (
            <AddPaymentRow
              nextNumber={nextNumber}
              pending={addPending}
              onConfirm={onAddConfirm}
              onCancel={onAddCancel}
            />
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
